import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Surface, eyebrowStyle, ghostLinkStyle, pageStyle, primaryButtonStyle } from '@kilat/ui';
import { Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import { useAuth } from '../lib/use-auth.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import {
  commissionRulesRepo,
  moneyPolicyRepo,
  plansRepo,
} from '../lib/repositories/platform-config-repo.js';
import { logger } from '../lib/logger.js';
import { PAGE_FRAME_MAX, dashboardPageStyle, sectionGridStyle } from '../lib/layout.js';
import {
  basisPointsFromPct,
  clampInt,
  numOrThrow,
  rmToSen,
  pctFrom,
  pctFromBasisPoints,
  pctToRate,
  pctToWholeRate,
  rmToSenStrict,
  senToRm,
} from '../lib/money-format.js';
import { IMPLEMENTED_PAYMENT_PROVIDERS } from '@kilat/shared';
import type {
  CommissionRules,
  MoneyPolicy,
  PaymentProvider,
  Plan,
  PlatformBrand,
} from '@kilat/shared';
import { platformBrandsRepo } from '../lib/repositories/platform-brands-repo.js';

/**
 * /superadmin/plans — Super-admin editor for subscription plans and
 * the platform-wide commission rules. Sprint 81, recruiter epic Sprint 2.
 *
 * Closes the gap from [[feedback-configurable-repeatable-parties]]:
 * before this sprint these were edited via the Firebase console. Now
 * they're live-editable from the dashboard by anyone with the
 * super-admin claim for the current brand.
 *
 * Three stacked cards:
 *   - Plans: one row per plan, in-line edit name + monthly price
 *     + extra-outlet price + signup fee + activation fee
 *   - Money policy (ADR-0033, added with the G23 slice-B editor):
 *     `platformConfig/moneyPolicy` — trial length, the toyyibPay fee
 *     table the customer pays, and the billing-lifecycle day counts.
 *     The server has read this doc live since ADR-0033; until this card
 *     existed nothing wrote it, so the values were reachable only by
 *     re-seeding or a hand edit in the Firebase console.
 *   - Commission rules: agent signup commission + signing bonus, the
 *     survival bonus, the ADR-0034 residual rates, per-tier multipliers,
 *     recruiter splits, and the operational settings (clawback window,
 *     payout threshold/day/method).
 *
 * Every state explicit per [[feedback-no-dead-ends]]: loading /
 * super-admin-gated / error / empty / saving / saved / failed.
 */

export function SuperadminPlansPage() {
  const { user, claims, loading: authLoading } = useAuth();
  const brand = usePlatformBrand();
  const isSuper = useMemo(() => isSuperAdminForBrand(claims, brand.id), [claims, brand.id]);

  const plansSubscribe = useMemo(() => {
    if (!isSuper) return null;
    return (cb: (vs: Plan[]) => void) => plansRepo.listSnapshot(cb);
  }, [isSuper]);

  const {
    data: plans,
    loading: plansLoading,
    error: plansError,
  } = useTrackedSnapshot<Plan[]>(['platform-plans', brand.id], plansSubscribe);

  const [rules, setRules] = useState<CommissionRules | null>(null);
  const [rulesLoading, setRulesLoading] = useState(true);

  useEffect(() => {
    if (!isSuper) {
      setRulesLoading(false);
      return;
    }
    setRulesLoading(true);
    const unsub = commissionRulesRepo.snapshot((next) => {
      setRules(next);
      setRulesLoading(false);
    });
    return () => unsub();
  }, [isSuper]);

  // ADR-0033 money policy. Three outcomes, kept distinct on purpose: the doc
  // read fine (`ready` + policy), the doc is not there yet (`ready` + null →
  // "run the seed"), or the read itself failed (`error` → say so). Collapsing
  // the last two into one empty state is how an owner ends up told to seed a
  // document that already exists.
  const [policy, setPolicy] = useState<MoneyPolicy | null>(null);
  const [policyStage, setPolicyStage] = useState<'loading' | 'ready' | 'error'>('loading');
  const [policyError, setPolicyError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSuper) {
      setPolicyStage('ready');
      return;
    }
    setPolicyStage('loading');
    setPolicyError(null);
    const unsub = moneyPolicyRepo.snapshot(
      (next) => {
        setPolicy(next);
        setPolicyStage('ready');
      },
      (err) => {
        logger.error('money policy listener failed', { error: String(err) });
        setPolicyError(err.message || String(err));
        setPolicyStage('error');
      },
    );
    return () => unsub();
  }, [isSuper]);

  if (authLoading) return <Screen title="Loading…" />;
  if (!isSuper) {
    return (
      <Screen
        title="Super-admin only"
        body={`/superadmin/plans is restricted to ${brand.name} super-admins. Sign in with a super-admin account, or head back to the queue.`}
        cta={{ label: 'Back to queue', to: '/queue' }}
      />
    );
  }

  return (
    <main style={dashboardPageStyle}>
      <header style={headerStyle}>
        <div style={{ maxWidth: PAGE_FRAME_MAX, margin: '0 auto', padding: 'var(--space-5)' }}>
          <p style={eyebrowStyle}>Super-admin · Plans, money policy + commission</p>
          <h1
            style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--text-h1)', fontWeight: 600 }}
          >
            Plans, money policy &amp; commission
          </h1>
          <p
            style={{
              margin: 'var(--space-2) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              maxWidth: 640,
            }}
          >
            Every money number the platform charges or pays, in one place: subscription prices and
            one-time fees, the free-trial length, the payment fee the customer sees at checkout, the
            grace/suspend/archive windows, and the splits that drive the agent and recruiter
            channel. Changes are live the moment you save — the server re-reads within five minutes,
            no deploy.
          </p>
          <div style={{ marginTop: 'var(--space-4)' }}>
            <Link to="/queue" style={ghostLinkStyle}>
              ← Back to queue
            </Link>
          </div>
        </div>
      </header>

      <section style={contentStyle}>
        <div style={twoColStyle}>
          <PlansSection plans={plans} loading={plansLoading} error={plansError} />
          <MoneyPolicySection
            policy={policy}
            stage={policyStage}
            errorMessage={policyError}
            editorUid={user?.uid ?? null}
          />
          <SupplierLegalSection brand={brand} />
          <RulesSection rules={rules} loading={rulesLoading} />
        </div>
      </section>
    </main>
  );
}

// ── Plans ─────────────────────────────────────────────────────────────

function PlansSection({
  plans,
  loading,
  error,
}: {
  readonly plans: readonly Plan[] | null;
  readonly loading: boolean;
  readonly error: Error | null;
}) {
  return (
    <Surface as="section">
      <h2 style={sectionTitleStyle}>Plans</h2>
      <p style={sectionHintStyle}>
        Subscription pricing + one-time signup &amp; activation fees per plan. Fees default to 0;
        set them to enable the on-boarding revenue layer per the recruiter epic.
      </p>
      <div style={{ marginTop: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)' }}>
        {loading || plans === null ? (
          <SkeletonRow />
        ) : error ? (
          <InlineNote tone="error">
            Could not load plans: {String(error.message ?? error)}
          </InlineNote>
        ) : plans.length === 0 ? (
          <InlineNote>
            No plans on file. The seed script writes the default <code>starter</code> plan on first
            deploy — run it once to bootstrap.
          </InlineNote>
        ) : (
          plans.map((p) => <PlanRow key={p.id} plan={p} />)
        )}
      </div>
    </Surface>
  );
}

function PlanRow({ plan }: { readonly plan: Plan }) {
  const [name, setName] = useState(plan.name);
  const [basePrice, setBasePrice] = useState(senToRm(plan.basePrice));
  const [extra, setExtra] = useState(senToRm(plan.extraOutletPrice));
  const [signup, setSignup] = useState(senToRm(plan.fees.signupSen));
  const [activation, setActivation] = useState(senToRm(plan.fees.activationSen));
  const [stage, setStage] = useState<
    { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'failed'; message: string }
  >({ kind: 'idle' });

  // Re-hydrate when upstream plan changes AND we're not currently editing
  useEffect(() => {
    if (stage.kind === 'saving') return;
    setName(plan.name);
    setBasePrice(senToRm(plan.basePrice));
    setExtra(senToRm(plan.extraOutletPrice));
    setSignup(senToRm(plan.fees.signupSen));
    setActivation(senToRm(plan.fees.activationSen));
    // intentionally omit `stage` from deps — we only want upstream-change re-hydration
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan]);

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    setStage({ kind: 'saving' });
    try {
      const basePriceSen = rmToSen(basePrice);
      const extraSen = rmToSen(extra);
      const signupSen = rmToSen(signup);
      const activationSen = rmToSen(activation);
      if (
        basePriceSen === null ||
        extraSen === null ||
        signupSen === null ||
        activationSen === null
      ) {
        setStage({ kind: 'failed', message: "Prices must look like '99' or '99.00' — sen only." });
        return;
      }
      const next: Plan = {
        ...plan,
        name: name.trim() || plan.name,
        basePrice: basePriceSen,
        extraOutletPrice: extraSen,
        fees: { signupSen, activationSen },
      };
      await plansRepo.set(plan.id, next);
      logger.info('plan saved', { planId: plan.id });
      setStage({ kind: 'saved' });
      setTimeout(() => setStage({ kind: 'idle' }), 1100);
    } catch (err) {
      logger.error('plan save failed', { planId: plan.id, error: String(err) });
      setStage({ kind: 'failed', message: (err as Error).message ?? 'Save failed' });
    }
  }

  return (
    <article style={planCardStyle}>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>{plan.name}</h3>
        <code
          style={{
            fontSize: 11,
            color: 'var(--color-on-surface-muted)',
            background: 'var(--tint-subtle)',
            padding: '2px 6px',
            borderRadius: 4,
            fontFamily: 'inherit',
          }}
        >
          {plan.id}
        </code>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 11,
            color: 'var(--color-on-surface-muted)',
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            fontWeight: 600,
          }}
        >
          {plan.currency}
        </span>
      </header>
      <form
        onSubmit={save}
        style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-3)' }}
      >
        <SmallField label="Plan name" value={name} onChange={setName} />
        <div style={twoCol}>
          <SmallField
            label="Base price / month (RM)"
            value={basePrice}
            onChange={setBasePrice}
            numeric
          />
          <SmallField label="Extra outlet / month (RM)" value={extra} onChange={setExtra} numeric />
        </div>
        <div style={twoCol}>
          <SmallField
            label="Signup fee (RM, one-time)"
            value={signup}
            onChange={setSignup}
            numeric
            hint="Charged on the operator's first paid invoice. 0 = off."
          />
          <SmallField
            label="Activation fee (RM, one-time)"
            value={activation}
            onChange={setActivation}
            numeric
            hint="Same as signup fee — separated for clearer per-fee splits."
          />
        </div>

        {stage.kind === 'failed' ? <InlineNote tone="error">{stage.message}</InlineNote> : null}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 'var(--space-3)',
          }}
        >
          {stage.kind === 'saved' ? (
            <span
              style={{
                color: 'var(--color-primary)',
                fontSize: 'var(--text-meta)',
                fontWeight: 600,
              }}
            >
              ✓ Saved.
            </span>
          ) : null}
          <button
            type="submit"
            disabled={stage.kind === 'saving'}
            style={{
              ...primaryButtonStyle,
              opacity: stage.kind === 'saving' ? 0.6 : 1,
            }}
          >
            {stage.kind === 'saving' ? 'Saving…' : 'Save plan'}
          </button>
        </div>
      </form>
    </article>
  );
}

// ── Money policy (ADR-0033) ──────────────────────────────

function MoneyPolicySection({
  policy,
  stage,
  errorMessage,
  editorUid,
}: {
  readonly policy: MoneyPolicy | null;
  readonly stage: 'loading' | 'ready' | 'error';
  readonly errorMessage: string | null;
  readonly editorUid: string | null;
}) {
  if (stage === 'loading') {
    return (
      <Surface as="section">
        <h2 style={sectionTitleStyle}>Money policy</h2>
        <p style={sectionHintStyle}>Reading the live policy from Firestore…</p>
        <div style={{ marginTop: 'var(--space-4)' }}>
          <SkeletonRow />
        </div>
      </Surface>
    );
  }

  if (stage === 'error') {
    return (
      <Surface as="section">
        <h2 style={sectionTitleStyle}>Money policy</h2>
        <div style={{ marginTop: 'var(--space-4)' }}>
          <InlineNote tone="error">
            Could not read <code>platformConfig/moneyPolicy</code>
            {errorMessage ? `: ${errorMessage}` : '.'} Nothing is charged wrongly in the meantime —
            the server falls back to the shipped numbers (14-day trial, FPX RM 1.00, card 2.5% + RM
            1.00 capped at RM 50.00, lifecycle 0 / 30 / 90, invoice due 14 days). Reload once you
            are back online; if it keeps failing, check this account still carries the super-admin
            claim for this brand.
          </InlineNote>
        </div>
      </Surface>
    );
  }

  if (!policy) {
    return (
      <Surface as="section">
        <h2 style={sectionTitleStyle}>Money policy</h2>
        <div style={{ marginTop: 'var(--space-4)' }}>
          <InlineNote>
            <code>platformConfig/moneyPolicy</code> has not been written yet, so there is nothing to
            edit here — an editor over invented values would be worse than none. Run{' '}
            <code>pnpm seed</code> once to create it with the shipped numbers (exactly what the
            server already falls back to, so seeding changes nothing anyone is charged), then reload
            this page.
          </InlineNote>
        </div>
      </Surface>
    );
  }

  return <MoneyPolicyEditor policy={policy} editorUid={editorUid} />;
}

function MoneyPolicyEditor({
  policy,
  editorUid,
}: {
  readonly policy: MoneyPolicy;
  readonly editorUid: string | null;
}) {
  const [trialDays, setTrialDays] = useState(String(policy.trial.days));

  // Which rail is live (ADR-0043). toyyibPay ships and is wired; Airwallex is
  // configurable but unimplemented, so `save` refuses it rather than accepting
  // a value the code cannot honour.
  const [provider, setProvider] = useState<PaymentProvider>(policy.psp.activeProvider);

  // toyyibPay — the ACTIVE rail's rates. Flat on FPX, percentage on cards.
  const [fpxFlat, setFpxFlat] = useState(senToRm(policy.psp.toyyibpay.fpxFlatSen));
  const [tpCardPct, setTpCardPct] = useState(
    pctFromBasisPoints(policy.psp.toyyibpay.cardPctBasisPoints),
  );
  const [tpCardFlat, setTpCardFlat] = useState(senToRm(policy.psp.toyyibpay.cardFlatSen));
  const [tpCardCap, setTpCardCap] = useState(senToRm(policy.psp.toyyibpay.cardCapSen));

  // Airwallex — dormant until selected. `localPct` is where toyyibPay had a
  // FLAT FPX fee: toyyibPay charges RM 1.00 regardless of price, Airwallex a
  // percentage, and under zero-cut the customer pays it. That single field is
  // what would change on a RM 150 detail if the rail ever switched.
  const [localPct, setLocalPct] = useState(
    pctFromBasisPoints(policy.psp.airwallex.localPctBasisPoints),
  );
  const [localFlat, setLocalFlat] = useState(senToRm(policy.psp.airwallex.localFlatSen));
  const [cardPct, setCardPct] = useState(
    pctFromBasisPoints(policy.psp.airwallex.cardPctBasisPoints),
  );
  const [cardFlat, setCardFlat] = useState(senToRm(policy.psp.airwallex.cardFlatSen));
  const [feeCap, setFeeCap] = useState(senToRm(policy.psp.airwallex.capSen));
  // Saving the form is the act of confirming the rates: a super-admin has just
  // read them against the real rate card. It is not a checkbox, because a
  // checkbox someone can tick without changing a number is a rubber stamp.
  const [rateConfirmed, setRateConfirmed] = useState(policy.psp.airwallex.confirmed);
  const [graceDays, setGraceDays] = useState(String(policy.billingLifecycle.pastDueGraceDays));
  const [suspendDays, setSuspendDays] = useState(String(policy.billingLifecycle.suspendAfterDays));
  const [archiveDays, setArchiveDays] = useState(
    String(policy.billingLifecycle.suspendedRetentionDays),
  );
  const [invoiceDueDays, setInvoiceDueDays] = useState(
    String(policy.billingLifecycle.invoiceDueDays),
  );

  // The platform's OWN SST status as supplier of the operator subscription.
  // Off by default: no SST on any operator invoice until this is filled in.
  const [sstRegistered, setSstRegistered] = useState(policy.supplierTax.sstRegistered);
  const [sstNumber, setSstNumber] = useState(policy.supplierTax.sstNumber);
  const [sstRatePct, setSstRatePct] = useState(pctFrom(policy.supplierTax.sstRate));

  const [stage, setStage] = useState<
    { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'failed'; message: string }
  >({ kind: 'idle' });

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    // Registered without a number would print a tax invoice with no SST
    // registration on it — refuse here, while a person is looking.
    if (sstRegistered && !sstNumber.trim()) {
      setStage({
        kind: 'failed',
        message:
          'Enter the SST registration number, or untick "registered". A tax invoice must carry the supplier’s SST number.',
      });
      return;
    }

    // Refuse an unimplemented rail rather than storing it and discovering the
    // gap when a customer tries to pay. The dropdown offers every provider the
    // platform knows about, because hiding one makes the roadmap invisible —
    // but choosing one whose client does not exist has to fail loudly, here,
    // while a person is looking at it.
    if (!IMPLEMENTED_PAYMENT_PROVIDERS.includes(provider)) {
      setStage({
        kind: 'failed',
        message:
          `${provider} is configured but not implemented yet — its fee table and credentials ` +
          `exist, its client and webhook do not. Saving it would leave the platform unable to ` +
          `take a payment. Keep toyyibPay active until that work lands.`,
      });
      return;
    }

    setStage({ kind: 'saving' });
    try {
      const next: MoneyPolicy = {
        ...policy,
        trial: { days: clampInt(numOrThrow(trialDays, 'Trial length'), 0, 365) },
        psp: {
          ...policy.psp,
          activeProvider: provider,
          toyyibpay: {
            fpxFlatSen: rmToSenStrict(fpxFlat, 'FPX flat fee'),
            cardPctBasisPoints: basisPointsFromPct(tpCardPct, 'toyyibPay card rate'),
            cardFlatSen: rmToSenStrict(tpCardFlat, 'toyyibPay card flat fee'),
            cardCapSen: rmToSenStrict(tpCardCap, 'toyyibPay card fee cap'),
          },
          airwallex: {
            localPctBasisPoints: basisPointsFromPct(localPct, 'Local-method rate'),
            localFlatSen: rmToSenStrict(localFlat, 'Local-method flat fee'),
            cardPctBasisPoints: basisPointsFromPct(cardPct, 'Card rate'),
            cardFlatSen: rmToSenStrict(cardFlat, 'Card flat fee'),
            capSen: rmToSenStrict(feeCap, 'Fee cap'),
            confirmed: rateConfirmed,
          },
        },
        billingLifecycle: {
          pastDueGraceDays: clampInt(numOrThrow(graceDays, 'Past-due grace'), 0, 90),
          suspendAfterDays: clampInt(numOrThrow(suspendDays, 'Suspend after'), 0, 365),
          suspendedRetentionDays: clampInt(numOrThrow(archiveDays, 'Archive after'), 1, 3_650),
          invoiceDueDays: clampInt(numOrThrow(invoiceDueDays, 'Invoice due window'), 0, 90),
        },
        supplierTax: {
          sstRegistered,
          sstNumber: sstNumber.trim(),
          sstRate: pctToRate(sstRatePct, 'SST rate'),
        },
        // Firestore Timestamp, never `new Date()` — and `moneyPolicyRepo.set`
        // writes this object rather than the Zod-parsed clone, so it survives.
        updatedAt: Timestamp.now(),
        ...(editorUid ? { updatedBy: editorUid } : {}),
      };
      await moneyPolicyRepo.set(next);
      logger.info('money policy saved');
      setStage({ kind: 'saved' });
      setTimeout(() => setStage({ kind: 'idle' }), 1100);
    } catch (err) {
      logger.error('money policy save failed', { error: String(err) });
      setStage({ kind: 'failed', message: (err as Error).message ?? 'Save failed' });
    }
  }

  return (
    <Surface as="section">
      <h2 style={sectionTitleStyle}>Money policy</h2>
      <p style={sectionHintStyle}>
        The money numbers that are neither a plan price nor a commission rule. Stored in{' '}
        <code>platformConfig/moneyPolicy</code> and re-read by the server within five minutes of a
        save. If that document is ever unreadable the server keeps using the shipped numbers rather
        than zero — an unreachable config file must never cancel a trial or quietly move a payment
        fee onto Kilat.
      </p>
      <form
        onSubmit={save}
        style={{ marginTop: 'var(--space-4)', display: 'grid', gap: 'var(--space-4)' }}
      >
        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>Trial</legend>
          <p style={smallFieldHint}>
            Applies to operators who sign up from now on. Anyone already in a trial keeps the end
            date stamped when their tenant was created, so changing this never shortens or extends a
            trial someone is currently sitting in.
          </p>
          <SmallField
            label="Trial length (days)"
            value={trialDays}
            onChange={setTrialDays}
            numeric
            hint="0 = no trial; the operator is billed from day one. Shipped default: 14."
          />
        </fieldset>

        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>Payment gateway</legend>
          <p style={smallFieldHint}>
            Which rail actually takes money. One at a time — running two means two reconciliation
            paths and a settlement question every time a figure disagrees. Switching is a setting,
            not a deploy, so Kilat is not locked to whichever gateway was wired first.
          </p>
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            {(['toyyibpay', 'airwallex'] as const).map((id) => {
              const ready = IMPLEMENTED_PAYMENT_PROVIDERS.includes(id);
              return (
                <label
                  key={id}
                  style={{
                    display: 'flex',
                    gap: 'var(--space-3)',
                    alignItems: 'flex-start',
                    padding: 'var(--space-3)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    background: provider === id ? 'var(--color-primary-soft)' : 'transparent',
                  }}
                >
                  <input
                    type="radio"
                    name="psp"
                    value={id}
                    checked={provider === id}
                    onChange={() => setProvider(id)}
                  />
                  <span>
                    <strong style={{ fontWeight: 600 }}>
                      {id === 'toyyibpay' ? 'toyyibPay' : 'Airwallex'}
                    </strong>
                    <span style={smallFieldHint}>
                      {ready
                        ? ' — wired end to end. FPX and cards, Kilat as Merchant of Record, so an operator never signs up for a gateway.'
                        : ' — configured but NOT built yet. Its fee table and credential fields exist; its client and webhook do not. Saving it is refused, because a gateway that cannot take a payment must fail here rather than at a customer’s checkout.'}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>
            Payment processing fee — toyyibPay{provider === 'toyyibpay' ? ' (live)' : ''}
          </legend>
          <p style={smallFieldHint}>
            toyyibPay’s published rates, mirrored here so a gateway price change is an edit instead
            of a deploy. The customer sees this as its own “Payment fee” line at checkout, so
            raising it raises what the CUSTOMER pays — it does not raise what Kilat keeps. Kilat
            still takes no cut of a wash and the operator still receives their full stated price.
            The app quotes the FPX rate, since that is the channel most customers here choose.
          </p>
          <div style={twoCol}>
            <SmallField
              label="FPX flat fee (RM)"
              value={fpxFlat}
              onChange={setFpxFlat}
              numeric
              hint="Charged on every online-banking payment whatever the amount. Shipped default: RM 1.00."
            />
            <SmallField
              label="Card rate (%)"
              value={tpCardPct}
              onChange={setTpCardPct}
              numeric
              hint="Percentage of the price on card payments. Half-points are kept, so 2.5 means 2.5%."
            />
          </div>
          <div style={twoCol}>
            <SmallField
              label="Card flat fee (RM)"
              value={tpCardFlat}
              onChange={setTpCardFlat}
              numeric
              hint="Added on top of the card percentage. Shipped default: RM 1.00."
            />
            <SmallField
              label="Card fee cap (RM)"
              value={tpCardCap}
              onChange={setTpCardCap}
              numeric
              hint="Ceiling on the whole card fee, percentage plus flat. Shipped default: RM 50.00."
            />
          </div>
        </fieldset>
        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>
            Payment processing fee — Airwallex
            {provider === 'airwallex' ? ' (live)' : ' (not active)'}
          </legend>
          <p style={smallFieldHint}>
            Kept ready so switching gateway is a setting rather than a project. Nothing here affects
            a customer while toyyibPay is the active rail — these numbers start mattering the moment
            Airwallex is selected, which is exactly when nobody remembers they were never checked.
          </p>
          <p style={smallFieldHint}>
            <strong>The fee changes SHAPE between these two gateways.</strong> toyyibPay charges a
            flat RM 1.00 on FPX whatever the wash costs. Airwallex charges a percentage, so the fee
            would grow with the price — a few sen on a RM 25 wash, several ringgit on a RM 150
            detailing package. Under the zero-cut rule the customer pays that, so it is a change
            they would see. Worth knowing before switching, not after.
          </p>
          {!rateConfirmed ? (
            <p style={{ ...smallFieldHint, color: 'var(--color-warning-fg, var(--color-accent))' }}>
              These are placeholder rates that nobody has checked against a real Airwallex rate
              card. Harmless while toyyibPay is active; the moment Airwallex is selected they would
              be quoting customers a fee we invented. Confirm them before switching.
            </p>
          ) : null}
          <div style={twoCol}>
            <SmallField
              label="Local rate (%) — FPX / DuitNow QR"
              value={localPct}
              onChange={setLocalPct}
              numeric
              hint="Percentage of the price on online-banking and QR payments. Half-points are kept, so 1.8 means 1.8%."
            />
            <SmallField
              label="Local flat fee (RM)"
              value={localFlat}
              onChange={setLocalFlat}
              numeric
              hint="Added on top of the local percentage. Often zero — leave it at 0 unless your rate card says otherwise."
            />
          </div>
          <div style={twoCol}>
            <SmallField
              label="Card rate (%)"
              value={cardPct}
              onChange={setCardPct}
              numeric
              hint="Percentage of the price on card payments. Half-points are kept, so 2.9 means 2.9%."
            />
            <SmallField
              label="Card flat fee (RM)"
              value={cardFlat}
              onChange={setCardFlat}
              numeric
              hint="Added on top of the card percentage."
            />
          </div>
          <div style={twoCol}>
            <SmallField
              label="Fee cap (RM)"
              value={feeCap}
              onChange={setFeeCap}
              numeric
              hint="Ceiling on the whole fee for one payment. A guard against a mistyped rate reaching a customer’s bill, not an Airwallex published cap."
            />
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
              <input
                type="checkbox"
                checked={rateConfirmed}
                onChange={(e) => setRateConfirmed(e.currentTarget.checked)}
              />
              <span style={smallFieldHint}>
                I have checked these against Airwallex’s actual rate card for this account. Live
                payments stay switched off until this is ticked.
              </span>
            </label>
          </div>
        </fieldset>

        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>Billing lifecycle</legend>
          <p style={smallFieldHint}>
            How long an operator who has not paid keeps working, and how long their data survives
            after that. Read by the nightly lifecycle job and by the monthly invoice run.
          </p>
          <div style={twoCol}>
            <SmallField
              label="Past-due grace (days)"
              value={graceDays}
              onChange={setGraceDays}
              numeric
              hint="Days after the due date before the operator flips active → past due. Shipped default: 0 — past due the moment the date passes."
            />
            <SmallField
              label="Suspend after (days)"
              value={suspendDays}
              onChange={setSuspendDays}
              numeric
              hint="Days an invoice may stay overdue before the operator flips past due → suspended and their staff lose access. Shipped default: 30."
            />
          </div>
          <div style={twoCol}>
            <SmallField
              label="Archive after (days)"
              value={archiveDays}
              onChange={setArchiveDays}
              numeric
              hint="Days a suspended operator is kept before archiving. Minimum 1 — archiving is destructive, so “immediately” is not on offer. Shipped default: 90."
            />
            <SmallField
              label="Invoice due window (days)"
              value={invoiceDueDays}
              onChange={setInvoiceDueDays}
              numeric
              hint="Days after the billing period closes that a new subscription invoice falls due. Shipped default: 14."
            />
          </div>
        </fieldset>

        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>Service tax (SST) on operator invoices</legend>
          <p style={smallFieldHint}>
            Controls whether the monthly subscription invoice the platform sends each operator adds
            SST and prints as a TAX INVOICE. On that invoice the platform is the SUPPLIER and the
            operator is the customer, so this follows the platform company’s own SST registration
            with Royal Malaysian Customs — never the operator’s. Charging SST while not registered
            is an offence; once registered it is owed on every invoice. Left unticked, no SST is
            charged. Applies to invoices generated after you save; issued invoices keep the status
            stamped on them. The company name, registration number and address printed as “FROM” are
            in the Invoice supplier card.
          </p>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
            <input
              type="checkbox"
              checked={sstRegistered}
              onChange={(e) => setSstRegistered(e.currentTarget.checked)}
            />
            <span style={smallFieldHint}>
              The platform company is registered for SST — add SST to every new operator invoice.
            </span>
          </label>
          {sstRegistered ? (
            <div style={twoCol}>
              <SmallField
                label="SST registration number"
                value={sstNumber}
                onChange={setSstNumber}
                hint="Printed on every tax invoice. Required when registered."
              />
              <SmallField
                label="SST rate (%)"
                value={sstRatePct}
                onChange={setSstRatePct}
                numeric
                hint="Service tax on the subscription. Default 8 — change only if Customs changes the rate."
              />
            </div>
          ) : null}
        </fieldset>

        {stage.kind === 'failed' ? <InlineNote tone="error">{stage.message}</InlineNote> : null}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ ...smallFieldHint, marginRight: 'auto' }}>
            {policy.updatedAt
              ? `Last saved ${formatSavedAt(policy.updatedAt)}.`
              : 'Never edited from here — these are the seeded numbers.'}
          </span>
          {stage.kind === 'saved' ? (
            <span
              style={{
                color: 'var(--color-primary)',
                fontSize: 'var(--text-meta)',
                fontWeight: 600,
              }}
            >
              ✓ Saved. Live for new signups and new invoices within five minutes.
            </span>
          ) : null}
          <button
            type="submit"
            disabled={stage.kind === 'saving'}
            style={{ ...primaryButtonStyle, opacity: stage.kind === 'saving' ? 0.6 : 1 }}
          >
            {stage.kind === 'saving' ? 'Saving…' : 'Save money policy'}
          </button>
        </div>
      </form>
    </Surface>
  );
}

// ── Invoice supplier (platform legal entity) ──────────────────────────

/**
 * The legal entity that SUPPLIES the operator subscription — printed as
 * "FROM" on every operator invoice PDF. Stored on the platform brand
 * (`platformBrands/{id}.legal`, ADR-0017) because it is the same entity the
 * legal pages name; written through the same `platformBrandsRepo.update`
 * path the launch-capability switches on /superadmin/setup use.
 */
function SupplierLegalSection({ brand }: { readonly brand: PlatformBrand }) {
  const [companyName, setCompanyName] = useState(brand.legal.companyName);
  const [companyRegistration, setCompanyRegistration] = useState(
    brand.legal.companyRegistration ?? '',
  );
  const [taxId, setTaxId] = useState(brand.legal.taxId ?? '');
  const [address, setAddress] = useState(brand.legal.address ?? '');
  const [stage, setStage] = useState<
    { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'failed'; message: string }
  >({ kind: 'idle' });

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!companyName.trim()) {
      setStage({ kind: 'failed', message: 'The registered company name cannot be blank.' });
      return;
    }
    setStage({ kind: 'saving' });
    try {
      await platformBrandsRepo.update(brand.id, {
        'legal.companyName': companyName.trim(),
        'legal.companyRegistration': companyRegistration.trim(),
        'legal.taxId': taxId.trim(),
        'legal.address': address.trim(),
      });
      logger.info('supplier legal entity saved');
      setStage({ kind: 'saved' });
      setTimeout(() => setStage({ kind: 'idle' }), 1100);
    } catch (err) {
      logger.error('supplier legal entity save failed', { error: String(err) });
      setStage({ kind: 'failed', message: (err as Error).message ?? 'Save failed' });
    }
  }

  return (
    <Surface as="section">
      <h2 style={sectionTitleStyle}>Invoice supplier</h2>
      <p style={sectionHintStyle}>
        The company that bills operators for their subscription. Printed as “FROM” on every operator
        invoice and named in the legal pages, so it must match the company’s SSM registration
        exactly. Whether those invoices carry SST is the “Service tax” setting in Money policy.
        Launch readiness stays incomplete until name, registration number and address are filled.
      </p>
      <form
        onSubmit={save}
        style={{ marginTop: 'var(--space-4)', display: 'grid', gap: 'var(--space-4)' }}
      >
        <div style={twoCol}>
          <SmallField
            label="Registered company name"
            value={companyName}
            onChange={setCompanyName}
            hint="Exactly as on the SSM certificate, e.g. “Kilat Sdn Bhd”."
          />
          <SmallField
            label="Company registration no. (SSM)"
            value={companyRegistration}
            onChange={setCompanyRegistration}
            hint="The 12-digit SSM number."
          />
        </div>
        <div style={twoCol}>
          <SmallField
            label="Tax identification no. (TIN)"
            value={taxId}
            onChange={setTaxId}
            hint="LHDN income-tax number. Optional today; needed for e-invoicing."
          />
          <SmallField
            label="Registered address"
            value={address}
            onChange={setAddress}
            hint="The registered business address from SSM."
          />
        </div>
        {stage.kind === 'failed' ? <InlineNote tone="error">{stage.message}</InlineNote> : null}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
          }}
        >
          {stage.kind === 'saved' ? (
            <span
              style={{
                color: 'var(--color-primary)',
                fontSize: 'var(--text-meta)',
                fontWeight: 600,
              }}
            >
              ✓ Saved. The next invoice PDF prints these details.
            </span>
          ) : null}
          <button
            type="submit"
            disabled={stage.kind === 'saving'}
            style={{ ...primaryButtonStyle, opacity: stage.kind === 'saving' ? 0.6 : 1 }}
          >
            {stage.kind === 'saving' ? 'Saving…' : 'Save supplier details'}
          </button>
        </div>
      </form>
    </Surface>
  );
}

// ── Rules ─────────────────────────────────────────────────────────────

function RulesSection({
  rules,
  loading,
}: {
  readonly rules: CommissionRules | null;
  readonly loading: boolean;
}) {
  if (loading) {
    return (
      <Surface as="section">
        <h2 style={sectionTitleStyle}>Commission rules</h2>
        <SkeletonRow />
      </Surface>
    );
  }
  if (!rules) {
    return (
      <Surface as="section">
        <h2 style={sectionTitleStyle}>Commission rules</h2>
        <InlineNote>
          <code>platformConfig/commissionRules</code> doesn't exist yet. Run the seed script once to
          create it with safe defaults.
        </InlineNote>
      </Surface>
    );
  }
  return <RulesEditor rules={rules} />;
}

function RulesEditor({ rules }: { readonly rules: CommissionRules }) {
  // Sprint 95 — one-time commission model. Agent earns a multiplier on
  // 1 month of subscription per signup. Recruiter earns a flat RM per
  // downstream signup. Legacy recurring fields are no longer surfaced.
  const [signupMultiplier, setSignupMultiplier] = useState(
    String(rules.signupCommissionMultiplier ?? 1),
  );
  const [recruiterPerSignupRm, setRecruiterPerSignupRm] = useState(
    senToRm(rules.recruiterPerSignupSen),
  );
  // ADR-0029 — survival bonus (retention-aligned). Defaults 0/90 = off.
  const [survivalMultiplier, setSurvivalMultiplier] = useState(
    String(rules.survivalBonusMultiplier ?? 0),
  );
  const [survivalThresholdDays, setSurvivalThresholdDays] = useState(
    String(rules.survivalBonusThresholdDays ?? 90),
  );
  const [recruiterSurvivalRm, setRecruiterSurvivalRm] = useState(
    senToRm(rules.recruiterSurvivalBonusSen),
  );
  const [bronze, setBronze] = useState(String(rules.tierMultipliers.bronze ?? 1));
  const [silver, setSilver] = useState(String(rules.tierMultipliers.silver ?? 1.1));
  const [gold, setGold] = useState(String(rules.tierMultipliers.gold ?? 1.25));
  const [recruiterBronze, setRecruiterBronze] = useState(
    String(rules.recruiterTierMultipliers.bronze ?? 1),
  );
  const [recruiterSilver, setRecruiterSilver] = useState(
    String(rules.recruiterTierMultipliers.silver ?? 1.1),
  );
  const [recruiterGold, setRecruiterGold] = useState(
    String(rules.recruiterTierMultipliers.gold ?? 1.25),
  );

  const [signupAgent, setSignupAgent] = useState(pctFrom(rules.firstInvoice.signupAgentSharePct));
  const [signupRecruiter, setSignupRecruiter] = useState(
    pctFrom(rules.firstInvoice.signupRecruiterSharePct),
  );
  const [actAgent, setActAgent] = useState(pctFrom(rules.firstInvoice.activationAgentSharePct));
  const [actRecruiter, setActRecruiter] = useState(
    pctFrom(rules.firstInvoice.activationRecruiterSharePct),
  );

  // ADR-0033 — a flat signing bonus, added AFTER the tier multiplier. Had a
  // real server consumer and no control until the G23 slice-B editor.
  const [signupBonusRm, setSignupBonusRm] = useState(senToRm(rules.signupBonus));

  // ADR-0034 — residual (trailing) commission. All three ship at 0 = off.
  const [recurringRate, setRecurringRate] = useState(pctFrom(rules.recurringRate));
  const [perExtraOutletRate, setPerExtraOutletRate] = useState(pctFrom(rules.perExtraOutletRate));
  const [recruiterRecurringRate, setRecruiterRecurringRate] = useState(
    pctFrom(rules.recruiterRecurringRate),
  );

  const [clawback, setClawback] = useState(String(rules.clawbackWindowDays));
  const [threshold, setThreshold] = useState(senToRm(rules.payoutThreshold));
  const [payoutDay, setPayoutDay] = useState(String(rules.payoutDayOfMonth));
  const [payoutMethod, setPayoutMethod] = useState<CommissionRules['payoutMethod']>(
    rules.payoutMethod,
  );

  const [stage, setStage] = useState<
    { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'failed'; message: string }
  >({ kind: 'idle' });

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStage({ kind: 'saving' });
    try {
      const next: CommissionRules = {
        ...rules,
        // Sprint 95 — one-time commission model
        signupCommissionMultiplier: Math.max(
          0,
          numOrThrow(signupMultiplier, 'signup commission multiplier'),
        ),
        recruiterPerSignupSen: rmToSenStrict(recruiterPerSignupRm, 'recruiter per-signup'),
        // ADR-0029 — survival bonus (0 multiplier / 0 sen = feature off)
        survivalBonusMultiplier: Math.max(
          0,
          numOrThrow(survivalMultiplier, 'survival bonus multiplier'),
        ),
        survivalBonusThresholdDays: clampInt(
          numOrThrow(survivalThresholdDays, 'survival threshold days'),
          1,
          365,
        ),
        recruiterSurvivalBonusSen: rmToSenStrict(recruiterSurvivalRm, 'recruiter survival bonus'),
        // ADR-0033 — flat signing bonus, applied after the tier multiplier.
        signupBonus: rmToSenStrict(signupBonusRm, 'signing bonus'),
        // ADR-0034 — residual commission. These were written as literal 0 on
        // every save while the layer was retired; they are the owner's numbers
        // again. 0 still means off, and off is still the default.
        recurringRate: pctToRate(recurringRate, 'Agent residual rate'),
        perExtraOutletRate: pctToRate(perExtraOutletRate, 'Agent growth residual rate'),
        recruiterRecurringRate: pctToRate(recruiterRecurringRate, 'Recruiter residual rate'),
        tierMultipliers: {
          bronze: numOrThrow(bronze, 'bronze multiplier'),
          silver: numOrThrow(silver, 'silver multiplier'),
          gold: numOrThrow(gold, 'gold multiplier'),
        },
        recruiterTierMultipliers: {
          bronze: numOrThrow(recruiterBronze, 'recruiter bronze multiplier'),
          silver: numOrThrow(recruiterSilver, 'recruiter silver multiplier'),
          gold: numOrThrow(recruiterGold, 'recruiter gold multiplier'),
        },
        firstInvoice: {
          signupAgentSharePct: pctToWholeRate(signupAgent, 'signup agent share'),
          signupRecruiterSharePct: pctToWholeRate(signupRecruiter, 'signup recruiter share'),
          activationAgentSharePct: pctToWholeRate(actAgent, 'activation agent share'),
          activationRecruiterSharePct: pctToWholeRate(actRecruiter, 'activation recruiter share'),
        },
        clawbackWindowDays: Math.max(0, Math.floor(numOrThrow(clawback, 'clawback window'))),
        payoutThreshold: rmToSenStrict(threshold, 'payout threshold'),
        payoutDayOfMonth: clampInt(numOrThrow(payoutDay, 'payout day'), 1, 28),
        payoutMethod,
      };
      await commissionRulesRepo.set(next);
      logger.info('commission rules saved');
      setStage({ kind: 'saved' });
      setTimeout(() => setStage({ kind: 'idle' }), 1100);
    } catch (err) {
      logger.error('commission rules save failed', { error: String(err) });
      setStage({ kind: 'failed', message: (err as Error).message ?? 'Save failed' });
    }
  }

  return (
    <Surface as="section">
      <h2 style={sectionTitleStyle}>Commission rules</h2>
      <p style={sectionHintStyle}>
        Three layers, and two of them stay off until you switch them on. Agents earn a multiplier on
        the operator's first month of subscription, paid once, plus an optional flat signing bonus.
        Recruiters earn a flat RM amount per downstream operator signup. On top of those sits the
        optional residual — a share of every later invoice while the operator keeps paying and the
        agent stays active. Everything here is clawed back if the operator refunds inside the
        clawback window, except the survival bonus, which is a milestone the operator genuinely
        reached.
      </p>
      <form
        onSubmit={save}
        style={{ marginTop: 'var(--space-4)', display: 'grid', gap: 'var(--space-4)' }}
      >
        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>Agent — signup commission</legend>
          <SmallField
            label="Multiplier on first-month subscription (×)"
            value={signupMultiplier}
            onChange={setSignupMultiplier}
            numeric
            hint="1.0 = agent earns 1 full month of the operator's subscription per signup. Multiplier × tier multiplier × subscription sum, floored."
          />
          <SmallField
            label="Signing bonus (RM, flat)"
            value={signupBonusRm}
            onChange={setSignupBonusRm}
            numeric
            hint="Added to the agent's one-time signup commission AFTER the tier multiplier, so every tier gets the same amount. 0 = off."
          />
          <div style={threeCol}>
            <SmallField label="Bronze multiplier (×)" value={bronze} onChange={setBronze} numeric />
            <SmallField label="Silver multiplier (×)" value={silver} onChange={setSilver} numeric />
            <SmallField label="Gold multiplier (×)" value={gold} onChange={setGold} numeric />
          </div>
        </fieldset>

        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>Agent — survival bonus (retention)</legend>
          <p style={sectionHintStyle}>
            A second one-time payment when a signed operator is still paying after the threshold
            below — re-points agents toward operators who stay. Leave the multiplier at 0 to keep it
            off.
          </p>
          <SmallField
            label="Multiplier on monthly subscription (×)"
            value={survivalMultiplier}
            onChange={setSurvivalMultiplier}
            numeric
            hint="0 = off. 1.0 = agent earns another full month of the operator's subscription when the operator survives to the threshold. Multiplier × tier multiplier × subscription sum, floored."
          />
          <div style={threeCol}>
            <SmallField
              label="Still-paying threshold (days)"
              value={survivalThresholdDays}
              onChange={setSurvivalThresholdDays}
              numeric
              hint="Days after the operator's first paid invoice. A later paid invoice at/after this day earns the bonus (default 90)."
            />
            <SmallField
              label="Recruiter survival bonus (RM)"
              value={recruiterSurvivalRm}
              onChange={setRecruiterSurvivalRm}
              numeric
              hint="Flat RM paid once to the recruiter (if any) when a downstream operator survives. 0 = off. × recruiter tier."
            />
          </div>
        </fieldset>

        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>Agent + recruiter — residual (trailing) commission</legend>
          <p style={sectionHintStyle}>
            A share of every paid invoice AFTER the operator's first, for as long as the operator
            keeps paying and the agent is still active. It needs no sweep and no end date: the
            operator stops paying and it stops, the agent leaves the channel and it stops. All three
            rates below are 0 out of the box, which means nothing accrues and no agent has been told
            anything — the layer is entirely off until you set a rate here.
          </p>
          <InlineNote>
            Worth thirty seconds before you type a number. A trailing rate is the one control on
            this page that only moves comfortably in one direction. Agents build a book of business
            on it, so raising it later is routine and welcome, while cutting it is the most damaging
            move available to a sales channel — it lands hardest on the agents who trusted it most.
            Pick a rate you would be content to keep paying for years, start on the low side, and
            move it up.
          </InlineNote>
          <div style={twoCol}>
            <SmallField
              label="Agent — share of each later invoice (%)"
              value={recurringRate}
              onChange={setRecurringRate}
              numeric
              hint="Applied to the subscription lines of every paid invoice after the first. 0 = off."
            />
            <SmallField
              label="Agent — share of growth lines (%)"
              value={perExtraOutletRate}
              onChange={setPerExtraOutletRate}
              numeric
              hint="Applied to extra-outlet and mid-month proration lines, so an agent earns more when their operator expands. 0 = off."
            />
          </div>
          <SmallField
            label="Recruiter — share of each later invoice (%)"
            value={recruiterRecurringRate}
            onChange={setRecruiterRecurringRate}
            numeric
            hint="Paid to the recruiter above the agent, on subscription lines from operators their agents signed. 0 = off."
          />
        </fieldset>

        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>Recruiter — per-signup commission</legend>
          <SmallField
            label="Flat amount per downstream signup (RM)"
            value={recruiterPerSignupRm}
            onChange={setRecruiterPerSignupRm}
            numeric
            hint="Paid once when an operator brought on by one of this recruiter's agents pays their first invoice. Multiplied by the recruiter's tier multiplier."
          />
          <div style={threeCol}>
            <SmallField
              label="Bronze multiplier (×)"
              value={recruiterBronze}
              onChange={setRecruiterBronze}
              numeric
            />
            <SmallField
              label="Silver multiplier (×)"
              value={recruiterSilver}
              onChange={setRecruiterSilver}
              numeric
            />
            <SmallField
              label="Gold multiplier (×)"
              value={recruiterGold}
              onChange={setRecruiterGold}
              numeric
            />
          </div>
        </fieldset>

        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>Optional — first-invoice fee splits</legend>
          <p style={smallFieldHint}>
            Only fires when the plan has signup or activation fees. Independent of the one-time
            signup commissions above. Leave all at 0 to skip.
          </p>
          <div style={twoCol}>
            <SmallField
              label="Signup → agent (%)"
              value={signupAgent}
              onChange={setSignupAgent}
              numeric
              hint="Agent's share of the signup fee."
            />
            <SmallField
              label="Signup → recruiter (%)"
              value={signupRecruiter}
              onChange={setSignupRecruiter}
              numeric
              hint="Recruiter's share of the signup fee."
            />
          </div>
          <div style={twoCol}>
            <SmallField
              label="Activation → agent (%)"
              value={actAgent}
              onChange={setActAgent}
              numeric
            />
            <SmallField
              label="Activation → recruiter (%)"
              value={actRecruiter}
              onChange={setActRecruiter}
              numeric
            />
          </div>
        </fieldset>

        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>Operational</legend>
          <div style={threeCol}>
            <SmallField
              label="Clawback window (days)"
              value={clawback}
              onChange={setClawback}
              numeric
            />
            <SmallField
              label="Payout threshold (RM)"
              value={threshold}
              onChange={setThreshold}
              numeric
            />
            <SmallField
              label="Payout day of month"
              value={payoutDay}
              onChange={setPayoutDay}
              numeric
            />
          </div>
          <SmallSelect
            label="Payout method"
            value={payoutMethod}
            onChange={setPayoutMethod}
            options={PAYOUT_METHOD_OPTIONS}
            hint="Which rail a payout uses when the recipient has both a DuitNow ID and bank details on file. If they only filled in one, that one is used whatever this says."
          />
        </fieldset>

        {stage.kind === 'failed' ? <InlineNote tone="error">{stage.message}</InlineNote> : null}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 'var(--space-3)',
          }}
        >
          {stage.kind === 'saved' ? (
            <span
              style={{
                color: 'var(--color-primary)',
                fontSize: 'var(--text-meta)',
                fontWeight: 600,
              }}
            >
              ✓ Saved.
            </span>
          ) : null}
          <button
            type="submit"
            disabled={stage.kind === 'saving'}
            style={{ ...primaryButtonStyle, opacity: stage.kind === 'saving' ? 0.6 : 1 }}
          >
            {stage.kind === 'saving' ? 'Saving…' : 'Save rules'}
          </button>
        </div>
      </form>
    </Surface>
  );
}

// ── Primitives + helpers ──────────────────────────────────────────────

function SmallField({
  label,
  value,
  onChange,
  numeric,
  hint,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly numeric?: boolean;
  readonly hint?: string;
}) {
  return (
    <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
      <span style={smallFieldLabel}>{label}</span>
      <input
        type="text"
        inputMode={numeric ? 'decimal' : 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={smallFieldInput}
      />
      {hint ? <span style={smallFieldHint}>{hint}</span> : null}
    </label>
  );
}

/**
 * Select sibling of {@link SmallField} — same label/hint furniture, same
 * styling, for the handful of settings that are an enum rather than a number.
 *
 * `onChange` hands back the matching option rather than casting the raw
 * `e.target.value`, so the literal union survives with no type assertion.
 */
function SmallSelect<T extends string>({
  label,
  value,
  onChange,
  options,
  hint,
}: {
  readonly label: string;
  readonly value: T;
  readonly onChange: (next: T) => void;
  readonly options: readonly { readonly value: T; readonly label: string }[];
  readonly hint?: string;
}) {
  return (
    <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
      <span style={smallFieldLabel}>{label}</span>
      <select
        value={value}
        onChange={(e) => {
          const picked = options.find((o) => o.value === e.target.value);
          if (picked) onChange(picked.value);
        }}
        style={{ ...smallFieldInput, cursor: 'pointer' }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {hint ? <span style={smallFieldHint}>{hint}</span> : null}
    </label>
  );
}

const PAYOUT_METHOD_OPTIONS: readonly {
  readonly value: CommissionRules['payoutMethod'];
  readonly label: string;
}[] = [
  { value: 'duitnow', label: 'DuitNow (instant, by phone/NRIC)' },
  { value: 'bank_transfer', label: 'Bank transfer (account number)' },
];

function InlineNote({
  children,
  tone = 'muted',
}: {
  readonly children: React.ReactNode;
  readonly tone?: 'muted' | 'error';
}) {
  return (
    <p
      role={tone === 'error' ? 'alert' : undefined}
      style={{
        margin: 0,
        padding: '10px 14px',
        borderRadius: 8,
        fontSize: 'var(--text-meta)',
        lineHeight: 1.5,
        background: tone === 'error' ? 'rgba(239,68,68,0.06)' : 'var(--tint-subtle)',
        border: `1px solid ${tone === 'error' ? 'rgba(239,68,68,0.20)' : 'var(--color-border)'}`,
        color: tone === 'error' ? '#991b1b' : 'var(--color-on-surface-muted)',
      }}
    >
      {children}
    </p>
  );
}

function SkeletonRow() {
  return (
    <div
      style={{
        height: 100,
        borderRadius: 12,
        background: 'var(--tint-subtle)',
        animation: 'kilatPulse 1.2s ease-in-out infinite',
      }}
    >
      <style>{`@keyframes kilatPulse { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }`}</style>
    </div>
  );
}

function Screen({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body?: string;
  readonly cta?: { label: string; to: string };
}) {
  return (
    <main style={{ ...pageStyle, display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center', maxWidth: 480, padding: 'var(--space-6)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h1>
        {body ? (
          <p
            style={{
              margin: 'var(--space-3) 0 0 0',
              color: 'var(--color-on-surface-muted)',
              lineHeight: 1.5,
            }}
          >
            {body}
          </p>
        ) : null}
        {cta ? (
          <Link
            to={cta.to}
            style={{
              display: 'inline-block',
              marginTop: 'var(--space-4)',
              padding: '12px 22px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-primary)',
              color: 'var(--color-primary-fg)',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            {cta.label}
          </Link>
        ) : null}
      </div>
    </main>
  );
}

function isSuperAdminForBrand(claims: Record<string, unknown> | null, brandId: string): boolean {
  if (!claims) return false;
  const sa = (claims as { superadmin?: Record<string, unknown> }).superadmin;
  if (!sa || typeof sa !== 'object') return false;
  return sa[brandId] === true;
}

/**
 * Display-only rendering of the policy's `updatedAt`. `toDate()` is optional on
 * the shared `Timestamp` interface — seed and test fixtures carry a bare
 * `{ seconds, nanoseconds }` with no prototype methods — so fall back to the
 * seconds field. Nothing here is ever stored; stored times are `Timestamp`.
 */
function formatSavedAt(ts: NonNullable<MoneyPolicy['updatedAt']>): string {
  const asDate = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
  return asDate.toLocaleString();
}

// ── Styles ────────────────────────────────────────────────────────────
const headerStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderBottom: '1px solid var(--color-border)',
};
const contentStyle: React.CSSProperties = {
  maxWidth: PAGE_FRAME_MAX,
  margin: '0 auto',
  padding: 'var(--space-5) var(--space-5) var(--space-12) var(--space-5)',
};
/** Plans, money policy and commission rules side by side once each fits 27.5rem. */
const twoColStyle: React.CSSProperties = sectionGridStyle;
const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-body)',
  fontWeight: 600,
};
const sectionHintStyle: React.CSSProperties = {
  margin: 'var(--space-2) 0 0 0',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.5,
};
const planCardStyle: React.CSSProperties = {
  padding: 'var(--space-4)',
  borderRadius: 12,
  background: 'var(--tint-subtle)',
  border: '1px solid var(--color-border)',
};
// `auto-fit` rather than a fixed column count so a field row collapses to one
// column on a 390px phone instead of squeezing three inputs into ~90px each.
// Empty tracks collapse under auto-fit, so a 2- or 3-field row still fills the
// card edge-to-edge on desktop exactly as the fixed grid did.
const twoCol: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))',
  gap: 'var(--space-3)',
};
const threeCol: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))',
  gap: 'var(--space-3)',
};
const fieldsetStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: 10,
  padding: 'var(--space-3) var(--space-4)',
  display: 'grid',
  gap: 'var(--space-3)',
};
const legendStyle: React.CSSProperties = {
  padding: '0 6px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: 'var(--color-on-surface-muted)',
};
const smallFieldLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--color-on-surface)',
  letterSpacing: 0.2,
};
const smallFieldInput: React.CSSProperties = {
  // width + border-box + minWidth:0 keep an input inside its grid track; a
  // bare <input> defaults to ~20 characters wide and would otherwise push the
  // card into horizontal scroll on a narrow phone.
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  padding: '8px 10px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'inherit',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  outline: 'none',
};
const smallFieldHint: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.4,
};
